import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import './AjustarRodada.css';

/**
 * Página para Admin ajustar rodada
 * Permite visualizar e editar todos os lançamentos de uma rodada
 */

const AjustarRodada = () => {
  const { id } = useParams();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [rodada, setRodada] = useState(null);
  const [participacoes, setParticipacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingParticipacao, setEditingParticipacao] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    carregarDados();
  }, [id, isAdmin, navigate]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      setError('');

      console.log('Carregando dados da rodada ID:', id);

      // Buscar rodadas primeiro
      const rodadasRes = await apiClient.get('/rodadas');
      console.log('Rodadas recebidas:', rodadasRes.data);
      console.log('Buscando rodada com ID:', id, 'tipo:', typeof id);
      
      const rodadaId = parseInt(id);
      const rodada = rodadasRes.data.find(r => {
        // Comparar tanto como número quanto como string para garantir compatibilidade
        const rId = typeof r.id === 'string' ? parseInt(r.id) : r.id;
        const match = rId === rodadaId || r.id === id || String(r.id) === String(id);
        console.log('Comparando rodada ID:', r.id, 'tipo:', typeof r.id, 'com:', rodadaId, 'match:', match);
        return match;
      });

      if (!rodada) {
        console.error('Rodada não encontrada. IDs disponíveis:', rodadasRes.data.map(r => r.id));
        setError(`Rodada com ID ${id} não encontrada. Verifique se a rodada existe.`);
        setLoading(false);
        return;
      }

      console.log('Rodada encontrada:', rodada);
      setRodada(rodada);

      // Buscar participações separadamente (pode falhar se não houver participações ainda)
      try {
        const participacoesRes = await apiClient.get(`/rodadas/${id}/participacoes-completas`);
        setParticipacoes(participacoesRes.data || []);
      } catch (partErr) {
        console.warn('Erro ao buscar participações (pode ser normal se não houver participações ainda):', partErr);
        setParticipacoes([]);
        // Não definir erro aqui, pois pode ser normal não ter participações ainda
      }
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Erro ao carregar dados da rodada';
      setError(errorMessage);
      
      // Se o erro for 404, tentar buscar apenas a rodada
      if (err.response?.status === 404) {
        try {
          const rodadasRes = await apiClient.get('/rodadas');
          const rodadaId = parseInt(id);
          const rodada = rodadasRes.data.find(r => r.id === rodadaId);
          if (rodada) {
            setRodada(rodada);
            setParticipacoes([]);
            setError('Rodada encontrada, mas não há participações ainda.');
          } else {
            setError(`Rodada com ID ${id} não encontrada na lista de rodadas.`);
          }
        } catch (err2) {
          console.error('Erro ao buscar rodada:', err2);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditarParticipacao = (participacao) => {
    setEditingParticipacao({
      ...participacao,
      // Garantir valores numéricos
      buyin_simples: participacao.buyin_simples || 1,
      buyin_duplo: participacao.buyin_duplo || 0,
      posicao_final: participacao.posicao_final || null,
    });
    setError('');
    setSuccess('');
  };

  const handleCancelarEdicao = () => {
    setEditingParticipacao(null);
    setError('');
    setSuccess('');
  };

  const handleSalvarParticipacao = async () => {
    if (!editingParticipacao) return;

    try {
      setSalvando(true);
      setError('');
      setSuccess('');

      // Validar valores
      if (editingParticipacao.buyin_simples < 1) {
        setError('Buy-in simples deve ser pelo menos 1 (buy-in inicial)');
        return;
      }

      if (editingParticipacao.buyin_duplo < 0) {
        setError('Buy-in duplo não pode ser negativo');
        return;
      }

      if (editingParticipacao.posicao_final !== null && editingParticipacao.posicao_final < 1) {
        setError('Posição final deve ser um número positivo');
        return;
      }

      // Atualizar participação via API
      await apiClient.put(`/rodadas/${id}/participacao/${editingParticipacao.id}`, {
        buyin_simples: parseInt(editingParticipacao.buyin_simples),
        buyin_duplo: parseInt(editingParticipacao.buyin_duplo),
        addon: editingParticipacao.addon || false,
        entrada_antecipada: editingParticipacao.entrada_antecipada || false,
        participou_janta: editingParticipacao.participou_janta || false,
        posicao_final: editingParticipacao.posicao_final ? parseInt(editingParticipacao.posicao_final) : null,
      });

      setSuccess('Participação atualizada com sucesso!');
      setEditingParticipacao(null);
      
      // Recarregar dados
      await carregarDados();
    } catch (err) {
      console.error('Erro ao salvar participação:', err);
      setError(err.response?.data?.error || 'Erro ao salvar participação');
    } finally {
      setSalvando(false);
    }
  };

  const handleAdicionarRebuy = async (participacaoId, tipo) => {
    try {
      await apiClient.post(`/rodadas/${id}/rebuy`, {
        jogador_id: participacoes.find(p => p.id === participacaoId)?.jogador_id,
        tipo: tipo,
      });
      setSuccess(`${tipo === 'simples' ? 'Rebuy simples' : 'Rebuy duplo'} adicionado com sucesso!`);
      await carregarDados();
    } catch (err) {
      console.error('Erro ao adicionar rebuy:', err);
      setError(err.response?.data?.error || 'Erro ao adicionar rebuy');
    }
  };

  const handleAdicionarAddon = async (participacaoId) => {
    try {
      await apiClient.post(`/rodadas/${id}/addon`, {
        jogador_id: participacoes.find(p => p.id === participacaoId)?.jogador_id,
      });
      setSuccess('Addon adicionado com sucesso!');
      await carregarDados();
    } catch (err) {
      console.error('Erro ao adicionar addon:', err);
      setError(err.response?.data?.error || 'Erro ao adicionar addon');
    }
  };

  const handleCancelarLancamento = async (participacaoId, tipo, quantidade = 1) => {
    const participacao = participacoes.find(p => p.id === participacaoId);
    if (!participacao) return;

    let mensagem = '';
    switch (tipo) {
      case 'rebuy_simples':
        mensagem = `Deseja cancelar ${quantidade} rebuy(s) simples de ${participacao.jogador?.nome}?`;
        break;
      case 'rebuy_duplo':
        mensagem = `Deseja cancelar ${quantidade} rebuy(s) duplo(s) de ${participacao.jogador?.nome}?`;
        break;
      case 'addon':
        mensagem = `Deseja cancelar o addon de ${participacao.jogador?.nome}?`;
        break;
      case 'janta':
        mensagem = `Deseja cancelar a participação na janta de ${participacao.jogador?.nome}?`;
        break;
      case 'entrada_antecipada':
        mensagem = `Deseja estornar a entrada antecipada de ${participacao.jogador?.nome}?`;
        break;
      default:
        mensagem = `Deseja cancelar este lançamento de ${participacao.jogador?.nome}?`;
    }

    if (!window.confirm(mensagem)) {
      return;
    }

    try {
      if (tipo === 'entrada_antecipada') {
        await apiClient.delete(`/rodadas/${id}/entrada-antecipada`, {
          data: { jogador_id: participacao.jogador_id },
        });
      } else {
        await apiClient.delete(`/rodadas/${id}/lancamento`, {
          data: { tipo, jogador_id: participacao.jogador_id, quantidade },
        });
      }
      setSuccess('Lançamento cancelado com sucesso!');
      await carregarDados();
    } catch (err) {
      console.error('Erro ao cancelar lançamento:', err);
      setError(err.response?.data?.error || 'Erro ao cancelar lançamento');
    }
  };

  const handleAdicionarEntradaAntecipada = async (participacaoId) => {
    const participacao = participacoes.find(p => p.id === participacaoId);
    if (!participacao) return;

    try {
      await apiClient.post(`/rodadas/${id}/entrada-antecipada`, {
        jogador_id: participacao.jogador_id,
      });
      setSuccess('Entrada antecipada adicionada com sucesso!');
      await carregarDados();
    } catch (err) {
      console.error('Erro ao adicionar entrada antecipada:', err);
      setError(err.response?.data?.error || 'Erro ao adicionar entrada antecipada');
    }
  };

  const handleMarcarJanta = async (participacaoId, participou) => {
    const participacao = participacoes.find(p => p.id === participacaoId);
    if (!participacao) return;

    try {
      // Atualizar participação para marcar/desmarcar janta
      await apiClient.put(`/rodadas/${id}/participacao/${participacaoId}`, {
        participou_janta: participou,
      });
      setSuccess(`Participação na janta ${participou ? 'adicionada' : 'removida'} com sucesso!`);
      await carregarDados();
    } catch (err) {
      console.error('Erro ao atualizar janta:', err);
      setError(err.response?.data?.error || 'Erro ao atualizar janta');
    }
  };

  const handleRemoverParticipacao = async (participacaoId) => {
    const participacao = participacoes.find(p => p.id === participacaoId);
    if (!participacao) return;

    const nomeJogador = participacao.jogador?.nome || 'este jogador';
    
    if (!window.confirm(
      `⚠️ ATENÇÃO: Deseja remover ${nomeJogador} desta rodada?\n\n` +
      `Esta ação irá:\n` +
      `- Cancelar a confirmação de presença\n` +
      `- Remover todos os consumos do bar relacionados\n` +
      `- Remover a participação completamente\n\n` +
      `Esta ação não pode ser desfeita facilmente.`
    )) {
      return;
    }

    try {
      setSalvando(true);
      setError('');
      
      await apiClient.delete(`/rodadas/${id}/jogador/${participacao.jogador_id}`);
      
      setSuccess(`${nomeJogador} removido da rodada com sucesso!`);
      await carregarDados();
    } catch (err) {
      console.error('Erro ao remover participação:', err);
      setError(err.response?.data?.error || 'Erro ao remover participação');
      alert(err.response?.data?.error || 'Erro ao remover participação');
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return <div className="container">Carregando...</div>;
  }

  if (!rodada) {
    return (
      <div className="container">
        <div className="error">
          <h2>Rodada não encontrada</h2>
          <p>ID da rodada: {id}</p>
          <p>Verifique se a rodada existe e se você tem permissão para acessá-la.</p>
          {error && <p style={{ color: 'red', marginTop: '10px' }}>Erro: {error}</p>}
        </div>
        <button onClick={() => navigate('/admin/menu')} className="btn btn-primary" style={{ marginTop: '20px' }}>
          Voltar para Menu Admin
        </button>
        <button onClick={() => navigate('/rodadas')} className="btn btn-secondary" style={{ marginTop: '10px', marginLeft: '10px' }}>
          Ver Todas as Rodadas
        </button>
      </div>
    );
  }

  const rodadaAberta = rodada.status === 'aberta';
  const buyinInicial = 1; // Sempre 1 buy-in inicial
  const rebuysSimples = (participacao) => Math.max(0, (participacao.buyin_simples || 1) - buyinInicial);
  const rebuysDuplos = (participacao) => participacao.buyin_duplo || 0;

  return (
    <div className="container ajustar-rodada-container">
      <div className="page-header">
        <h1>⚙️ Ajustar Rodada</h1>
        <button onClick={() => navigate('/admin/menu')} className="btn btn-secondary">
          ← Voltar
        </button>
      </div>

      {/* Informações da Rodada */}
      <div className="rodada-info-card">
        <h2>Informações da Rodada</h2>
        <div className="info-grid">
          <div>
            <strong>Data:</strong> {new Date(rodada.data).toLocaleDateString('pt-BR')}
          </div>
          <div>
            <strong>Status:</strong> 
            <span className={`status-badge ${rodada.status}`}>
              {rodada.status === 'aberta' ? '🟢 Aberta' : '🔴 Fechada'}
            </span>
          </div>
          <div>
            <strong>Participantes:</strong> {participacoes.length}
          </div>
        </div>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      {/* Tabela de Participações */}
      <div className="participacoes-section">
        <h2>Participações e Lançamentos</h2>
        <p className="info-text">
          {rodadaAberta 
            ? '⚠️ Rodada em andamento - Você pode fazer ajustes nos lançamentos'
            : '🔴 Rodada fechada - Ajustes limitados'}
        </p>

        <table className="participacoes-table">
          <thead>
            <tr>
              <th>Jogador</th>
              <th>Entrada Antecipada</th>
              <th>Buy-in Inicial</th>
              <th>Rebuys Simples</th>
              <th>Rebuys Duplos</th>
              <th>Addon</th>
              <th>Janta</th>
              <th>Posição Final</th>
              <th>Nocautes</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {participacoes.length === 0 ? (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center', padding: '20px' }}>
                  Nenhuma participação encontrada
                </td>
              </tr>
            ) : (
              participacoes.map((participacao) => {
                const isEditing = editingParticipacao?.id === participacao.id;
                const rebuysSimplesQtd = rebuysSimples(participacao);
                const rebuysDuplosQtd = rebuysDuplos(participacao);

                return (
                  <tr key={participacao.id} className={isEditing ? 'editing-row' : ''}>
                    <td><strong>{participacao.jogador?.nome || 'Jogador'}</strong></td>
                    
                    {/* Entrada Antecipada */}
                    <td style={{ textAlign: 'center' }}>
                      {isEditing ? (
                        <input
                          type="checkbox"
                          checked={editingParticipacao.entrada_antecipada || false}
                          onChange={(e) => setEditingParticipacao({
                            ...editingParticipacao,
                            entrada_antecipada: e.target.checked,
                          })}
                        />
                      ) : (
                        <>
                          {participacao.entrada_antecipada ? (
                            <span style={{ color: '#4caf50', fontWeight: 'bold' }}>✓</span>
                          ) : (
                            <span style={{ color: '#999' }}>-</span>
                          )}
                          {rodadaAberta && !participacao.entrada_antecipada && (
                            <button
                              onClick={() => handleAdicionarEntradaAntecipada(participacao.id)}
                              className="btn btn-sm btn-success"
                              style={{ marginLeft: '5px', padding: '2px 6px', fontSize: '10px' }}
                              title="Adicionar entrada antecipada"
                            >
                              +
                            </button>
                          )}
                          {rodadaAberta && participacao.entrada_antecipada && (
                            <button
                              onClick={() => handleCancelarLancamento(participacao.id, 'entrada_antecipada')}
                              className="btn btn-sm btn-danger"
                              style={{ marginLeft: '5px', padding: '2px 6px', fontSize: '10px' }}
                              title="Estornar entrada antecipada"
                            >
                              🗑️
                            </button>
                          )}
                        </>
                      )}
                    </td>

                    {/* Buy-in Inicial */}
                    <td style={{ textAlign: 'center' }}>
                      {isEditing ? (
                        <span>1 (fixo)</span>
                      ) : (
                        <span>1</span>
                      )}
                    </td>

                    {/* Rebuys Simples */}
                    <td style={{ textAlign: 'center' }}>
                      {isEditing ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'center' }}>
                          <input
                            type="number"
                            min="0"
                            value={rebuysSimplesQtd}
                            onChange={(e) => {
                              const novoRebuys = parseInt(e.target.value) || 0;
                              const novoBuyinSimples = buyinInicial + novoRebuys;
                              setEditingParticipacao({
                                ...editingParticipacao,
                                buyin_simples: novoBuyinSimples,
                              });
                            }}
                            style={{ width: '60px', textAlign: 'center' }}
                          />
                          <small>Total: {editingParticipacao.buyin_simples}</small>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', justifyContent: 'center' }}>
                          <span>{rebuysSimplesQtd}</span>
                          {rodadaAberta && (
                            <>
                              <button
                                onClick={() => handleAdicionarRebuy(participacao.id, 'simples')}
                                className="btn btn-sm btn-success"
                                style={{ padding: '2px 6px', fontSize: '10px' }}
                                title="Adicionar rebuy simples"
                              >
                                +
                              </button>
                              {rebuysSimplesQtd > 0 && (
                                <button
                                  onClick={() => handleCancelarLancamento(participacao.id, 'rebuy_simples', 1)}
                                  className="btn btn-sm btn-danger"
                                  style={{ padding: '2px 6px', fontSize: '10px' }}
                                  title="Cancelar 1 rebuy simples"
                                >
                                  -
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </td>

                    {/* Rebuys Duplos */}
                    <td style={{ textAlign: 'center' }}>
                      {isEditing ? (
                        <input
                          type="number"
                          min="0"
                          value={rebuysDuplosQtd}
                          onChange={(e) => setEditingParticipacao({
                            ...editingParticipacao,
                            buyin_duplo: parseInt(e.target.value) || 0,
                          })}
                          style={{ width: '60px', textAlign: 'center' }}
                        />
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', justifyContent: 'center' }}>
                          <span>{rebuysDuplosQtd}</span>
                          {rodadaAberta && (
                            <>
                              <button
                                onClick={() => handleAdicionarRebuy(participacao.id, 'duplo')}
                                className="btn btn-sm btn-success"
                                style={{ padding: '2px 6px', fontSize: '10px' }}
                                title="Adicionar rebuy duplo"
                              >
                                +
                              </button>
                              {rebuysDuplosQtd > 0 && (
                                <button
                                  onClick={() => handleCancelarLancamento(participacao.id, 'rebuy_duplo', 1)}
                                  className="btn btn-sm btn-danger"
                                  style={{ padding: '2px 6px', fontSize: '10px' }}
                                  title="Cancelar 1 rebuy duplo"
                                >
                                  -
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </td>

                    {/* Addon */}
                    <td style={{ textAlign: 'center' }}>
                      {isEditing ? (
                        <input
                          type="checkbox"
                          checked={editingParticipacao.addon || false}
                          onChange={(e) => setEditingParticipacao({
                            ...editingParticipacao,
                            addon: e.target.checked,
                          })}
                        />
                      ) : (
                        <>
                          {participacao.addon ? (
                            <span style={{ color: '#4caf50', fontWeight: 'bold' }}>✓</span>
                          ) : (
                            <span style={{ color: '#999' }}>-</span>
                          )}
                          {rodadaAberta && (
                            <>
                              {!participacao.addon && (
                                <button
                                  onClick={() => handleAdicionarAddon(participacao.id)}
                                  className="btn btn-sm btn-success"
                                  style={{ marginLeft: '5px', padding: '2px 6px', fontSize: '10px' }}
                                  title="Adicionar addon"
                                >
                                  +
                                </button>
                              )}
                              {participacao.addon && (
                                <button
                                  onClick={() => handleCancelarLancamento(participacao.id, 'addon')}
                                  className="btn btn-sm btn-danger"
                                  style={{ marginLeft: '5px', padding: '2px 6px', fontSize: '10px' }}
                                  title="Cancelar addon"
                                >
                                  🗑️
                                </button>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </td>

                    {/* Janta */}
                    <td style={{ textAlign: 'center' }}>
                      {isEditing ? (
                        <input
                          type="checkbox"
                          checked={editingParticipacao.participou_janta || false}
                          onChange={(e) => setEditingParticipacao({
                            ...editingParticipacao,
                            participou_janta: e.target.checked,
                          })}
                        />
                      ) : (
                        <>
                          {participacao.participou_janta ? (
                            <span style={{ color: '#4caf50', fontWeight: 'bold' }}>✓</span>
                          ) : (
                            <span style={{ color: '#999' }}>-</span>
                          )}
                          {rodadaAberta && (
                            <button
                              onClick={() => handleMarcarJanta(participacao.id, !participacao.participou_janta)}
                              className="btn btn-sm btn-secondary"
                              style={{ marginLeft: '5px', padding: '2px 6px', fontSize: '10px' }}
                              title={participacao.participou_janta ? 'Remover da janta' : 'Adicionar à janta'}
                            >
                              {participacao.participou_janta ? '🗑️' : '+'}
                            </button>
                          )}
                        </>
                      )}
                    </td>

                    {/* Posição Final */}
                    <td style={{ textAlign: 'center' }}>
                      {isEditing ? (
                        <input
                          type="number"
                          min="1"
                          value={editingParticipacao.posicao_final || ''}
                          onChange={(e) => setEditingParticipacao({
                            ...editingParticipacao,
                            posicao_final: e.target.value ? parseInt(e.target.value) : null,
                          })}
                          placeholder="Posição"
                          style={{ width: '70px', textAlign: 'center' }}
                        />
                      ) : (
                        <span>{participacao.posicao_final || '-'}</span>
                      )}
                    </td>

                    {/* Nocautes */}
                    <td style={{ textAlign: 'center' }}>
                      {participacao.nocauteou_ids && Array.isArray(participacao.nocauteou_ids) 
                        ? participacao.nocauteou_ids.length 
                        : 0}
                    </td>

                    {/* Ações */}
                    <td>
                      {isEditing ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                          <button
                            onClick={handleSalvarParticipacao}
                            disabled={salvando}
                            className="btn btn-sm btn-success"
                            style={{ padding: '3px 8px', fontSize: '11px' }}
                          >
                            {salvando ? 'Salvando...' : '✓ Salvar'}
                          </button>
                          <button
                            onClick={handleCancelarEdicao}
                            className="btn btn-sm btn-secondary"
                            style={{ padding: '3px 8px', fontSize: '11px' }}
                          >
                            ✕ Cancelar
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'center' }}>
                          <button
                            onClick={() => handleEditarParticipacao(participacao)}
                            disabled={!rodadaAberta || salvando}
                            className="btn btn-sm btn-primary"
                            style={{ padding: '3px 8px', fontSize: '11px', width: '100%' }}
                            title={rodadaAberta ? 'Editar participação' : 'Apenas rodadas abertas podem ser editadas'}
                          >
                            ✏️ Editar
                          </button>
                          {rodadaAberta && (
                            <button
                              onClick={() => handleRemoverParticipacao(participacao.id)}
                              disabled={salvando}
                              className="btn btn-sm btn-danger"
                              style={{ padding: '3px 8px', fontSize: '11px', width: '100%' }}
                              title="Remover jogador da rodada (cancelar participação)"
                            >
                              🗑️ Remover
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Informações Adicionais */}
      {rodada.jackpot_ativado && (
        <div className="info-card" style={{ marginTop: '20px', padding: '15px', background: '#fff3cd', borderRadius: '8px' }}>
          <h3>🎰 Jackpot</h3>
          <p><strong>Status:</strong> {rodada.jackpot_ativado ? 'Ativado' : 'Desativado'}</p>
          {rodada.jackpot_ganhador_id && (
            <p>
              <strong>Ganhador:</strong>{' '}
              {participacoes.find(p => p.jogador_id === rodada.jackpot_ganhador_id)?.jogador?.nome || 'Não encontrado'}
            </p>
          )}
        </div>
      )}

      {rodada.custo_janta && rodada.custo_janta > 0 && (
        <div className="info-card" style={{ marginTop: '20px', padding: '15px', background: '#d4edda', borderRadius: '8px' }}>
          <h3>🍽️ Janta</h3>
          <p><strong>Custo Total:</strong> R$ {parseFloat(rodada.custo_janta).toFixed(2)}</p>
          <p>
            <strong>Participantes:</strong>{' '}
            {participacoes.filter(p => p.participou_janta).length}
          </p>
        </div>
      )}
    </div>
  );
};

export default AjustarRodada;
